/**
 * HIPAA-compliant hash-chain audit trail.
 * Uses localStorage for storage, Web Crypto API for SHA-256 hashing.
 * Each entry's hash = SHA-256(prev_hash + action + detail + timestamp).
 * Zero network calls -- everything runs on-device.
 */

import type { AuditEntry } from '../types/index';

const LS_KEY = 'aftercare:audit_log';
const encoder = new TextEncoder();

async function sha256(input: string): Promise<string> {
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function getEntries(): AuditEntry[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveEntries(entries: AuditEntry[]): void {
  localStorage.setItem(LS_KEY, JSON.stringify(entries));
}

export async function logAction(action: string, detail: string): Promise<void> {
  const entries = getEntries();
  const id = crypto.randomUUID();
  const timestamp = new Date().toISOString();
  const prevHash = entries.length > 0 ? entries[entries.length - 1].entry_hash : '';
  const entryHash = await sha256(prevHash + action + detail + timestamp);

  entries.push({ id, action, detail, timestamp, prev_hash: prevHash, entry_hash: entryHash });
  saveEntries(entries);
}

export async function verifyChain(): Promise<{ valid: boolean; brokenAt?: number }> {
  const entries = getEntries();
  if (entries.length === 0) return { valid: true };

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (i === 0 && entry.prev_hash !== '') return { valid: false, brokenAt: 0 };
    if (i > 0 && entry.prev_hash !== entries[i - 1].entry_hash) return { valid: false, brokenAt: i };

    const expected = await sha256(entry.prev_hash + entry.action + entry.detail + entry.timestamp);
    if (entry.entry_hash !== expected) return { valid: false, brokenAt: i };
  }

  return { valid: true };
}

export async function getRecentLogs(limit: number = 50): Promise<AuditEntry[]> {
  return getEntries().slice(-limit).reverse();
}
